// ---------------------------------------------------------------------------
// Procedural audio and the shared replay output bus.
//
// The replay's short recorded cues use this same bus, mute setting and
// procedural fallbacks. Nothing is fetched until a replay opens.
// ---------------------------------------------------------------------------

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let noiseBuffer: AudioBuffer | null = null;
let muted = false;
/**
 * Whether the game plays sound at all: the owner's switch in the simulation
 * room (D37), told by the server in `welcome` and whenever it changes. Off
 * until it says otherwise, so a page never makes a sound the owner turned off.
 * The player's own mute (`muted`) applies on top of it.
 */
let allowed = false;

const MUTE_KEY = 'tb.muted';

try {
  muted = localStorage.getItem(MUTE_KEY) === '1';
} catch {
  muted = false;
}

/** Whether nothing should sound: the player muted it, or the game's sound is off (D37). */
export function isMuted(): boolean {
  return muted || !allowed;
}

/** The player's own choice in Settings, whatever the game's switch says. */
export function playerMuted(): boolean {
  return muted;
}

/** Whether the game plays sound at all (D37). */
export function soundAllowed(): boolean {
  return allowed;
}

export function setSoundAllowed(v: boolean): void {
  allowed = v;
  applyGain();
}

export function setMuted(v: boolean): void {
  muted = v;
  try {
    localStorage.setItem(MUTE_KEY, v ? '1' : '0');
  } catch {
    /* private browsing; the setting just will not persist */
  }
  applyGain();
}

function applyGain(): void {
  if (master && ctx) master.gain.setTargetAtTime(isMuted() ? 0 : 0.9, ctx.currentTime, 0.02);
}

/** Browsers need a user gesture before their audio context can run. */
export function initAudio(): void {
  if (ctx) {
    if (ctx.state === 'suspended') void ctx.resume();
    return;
  }
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = isMuted() ? 0 : 0.9;
    master.connect(ctx.destination);

    // One second of white noise, reused by everything that needs texture.
    const len = ctx.sampleRate;
    noiseBuffer = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  } catch {
    ctx = null;
  }
}

// A replay opens on the server's clock, without a click of its own. The first
// player gesture unlocks audio so that the later automatic transition can play.
if (typeof window !== 'undefined') {
  const unlock = () => {
    initAudio();
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
  };
  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);
}

/** Shared output for optional recorded cues; procedural sounds still work alone. */
export function audioOutput(): { context: AudioContext; destination: GainNode } | null {
  return ctx && master ? { context: ctx, destination: master } : null;
}

function now(): number {
  return ctx ? ctx.currentTime : 0;
}

function noise(): AudioBufferSourceNode | null {
  if (!ctx || !noiseBuffer) return null;
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer;
  src.loop = true;
  return src;
}

function env(gain: GainNode, t: number, peak: number, attack: number, decay: number): void {
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), t + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
}

// --- the sounds ------------------------------------------------------------

/** Air-raid siren. Two rising-falling cycles, deliberately unhurried. */
export function alarm(): void {
  if (!ctx || !master || isMuted()) return;
  const t = now();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 1400;

  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(300, t);
  // Two slow sweeps. The falling half is the part that sits in your chest.
  osc.frequency.linearRampToValueAtTime(560, t + 0.85);
  osc.frequency.linearRampToValueAtTime(300, t + 1.7);
  osc.frequency.linearRampToValueAtTime(560, t + 2.55);
  osc.frequency.linearRampToValueAtTime(300, t + 3.4);

  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.14, t + 0.3);
  gain.gain.setValueAtTime(0.14, t + 3.0);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 3.9);

  osc.connect(filter).connect(gain).connect(master);
  osc.start(t);
  osc.stop(t + 4);
}

/** A low bed of noise under the whole sequence, so silence never feels safe. */
export function rumble(duration: number): () => void {
  if (!ctx || !master || isMuted()) return () => {};
  const t = now();
  const src = noise();
  if (!src) return () => {};
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 110;

  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.10, t + 1.2);

  src.connect(filter).connect(gain).connect(master);
  src.start(t);

  const stop = () => {
    if (!ctx) return;
    const s = now();
    gain.gain.cancelScheduledValues(s);
    gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), s);
    gain.gain.exponentialRampToValueAtTime(0.0001, s + 0.8);
    try { src.stop(s + 0.9); } catch { /* already stopped */ }
  };
  setTimeout(stop, duration);
  return stop;
}

/** A launcher firing: hard noise transient with a fast downward sweep. */
export function samLaunch(): void {
  if (!ctx || !master || isMuted()) return;
  const t = now();
  const src = noise();
  if (!src) return;
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.Q.value = 1.1;
  filter.frequency.setValueAtTime(2600, t);
  filter.frequency.exponentialRampToValueAtTime(420, t + 0.42);

  env(gain, t, 0.16, 0.012, 0.42);
  src.connect(filter).connect(gain).connect(master);
  src.start(t);
  src.stop(t + 0.55);
}

/** Distinct C-RAM fallback while the recorded burst is still loading. */
export function cramBurst(): void {
  if (!ctx || !master || isMuted()) return;
  const t = now();
  const src = noise();
  if (!src) return;
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 1050;
  filter.Q.value = 0.7;
  gain.gain.setValueAtTime(0.0001, t);
  for (let i = 0; i < 10; i++) {
    const at = t + i * 0.053;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.linearRampToValueAtTime(0.16, at + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.036);
  }
  src.connect(filter).connect(gain).connect(master);
  src.start(t);
  src.stop(t + 0.65);
}

/** A restrained burst of interference when an EW belt contacts a round. */
export function ewPulse(): void {
  if (!ctx || !master || isMuted()) return;
  const t = now();
  const src = noise();
  if (!src) return;
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 1150;
  filter.Q.value = 0.75;
  env(gain, t, 0.07, 0.02, 0.35);
  src.connect(filter).connect(gain).connect(master);
  src.start(t);
  src.stop(t + 0.43);
}

/** A kill: a bright, dry crack a long way off. */
export function intercept(): void {
  if (!ctx || !master || isMuted()) return;
  const t = now();
  const src = noise();
  if (!src) return;
  const gain = ctx.createGain();
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 900;

  env(gain, t, 0.2, 0.004, 0.20);
  src.connect(hp).connect(gain).connect(master);
  src.start(t);
  src.stop(t + 0.3);

  // A little body underneath so it does not sound like static.
  const osc = ctx.createOscillator();
  const og = ctx.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(420, t);
  osc.frequency.exponentialRampToValueAtTime(120, t + 0.18);
  env(og, t, 0.1, 0.004, 0.18);
  osc.connect(og).connect(master);
  osc.start(t);
  osc.stop(t + 0.25);
}

/**
 * A warhead going off. `size` 0..1 scales the weight of it — a strike drone is
 * a bang, a ballistic missile is something you feel through the floor.
 */
export function impact(size: number): void {
  if (!ctx || !master || isMuted()) return;
  const t = now();
  const s = Math.max(0.25, Math.min(1, size));

  // Sub-bass drop: the part that reads as weight rather than volume.
  const sub = ctx.createOscillator();
  const subGain = ctx.createGain();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(90 * (0.7 + 0.5 * s), t);
  sub.frequency.exponentialRampToValueAtTime(22, t + 0.55 * s + 0.2);
  env(subGain, t, 0.42 * s, 0.008, 0.75 * s + 0.25);
  sub.connect(subGain).connect(master);
  sub.start(t);
  sub.stop(t + 1.4);

  // The blast itself.
  const src = noise();
  if (src) {
    const gain = ctx.createGain();
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(2200 * s + 400, t);
    lp.frequency.exponentialRampToValueAtTime(180, t + 0.6 * s + 0.2);
    env(gain, t, 0.3 * s, 0.006, 0.6 * s + 0.25);
    src.connect(lp).connect(gain).connect(master);
    src.start(t);
    src.stop(t + 1.2);
  }

  // Debris tail on the heavier hits.
  if (s > 0.55) {
    const tail = noise();
    if (tail) {
      const tg = ctx.createGain();
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 1800;
      tg.gain.setValueAtTime(0.0001, t + 0.12);
      tg.gain.exponentialRampToValueAtTime(0.05 * s, t + 0.2);
      tg.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
      tail.connect(bp).connect(tg).connect(master);
      tail.start(t + 0.12);
      tail.stop(t + 1.2);
    }
  }
}

/** A chemical payload disperses rather than producing a conventional blast. */
export function chemicalRelease(): void {
  if (!ctx || !master || isMuted()) return;
  const t = now();
  const src = noise();
  if (!src) return;
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(900, t);
  filter.frequency.linearRampToValueAtTime(1600, t + 1.1);
  filter.Q.value = 0.4;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.11, t + 0.16);
  gain.gain.setValueAtTime(0.11, t + 0.95);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 2.6);
  src.connect(filter).connect(gain).connect(master);
  src.start(t);
  src.stop(t + 2.65);
}

/** A munition of yours going off the air, somewhere out in the dark. */
export function lostContact(): void {
  if (!ctx || !master || isMuted()) return;
  const t = now();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(700, t);
  osc.frequency.exponentialRampToValueAtTime(180, t + 0.22);
  env(gain, t, 0.07, 0.005, 0.22);
  osc.connect(gain).connect(master);
  osc.start(t);
  osc.stop(t + 0.3);
}

/** The long single note at the end. Not relief, exactly. */
export function allClear(): void {
  if (!ctx || !master || isMuted()) return;
  const t = now();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = 196;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.09, t + 0.4);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 2.4);
  osc.connect(gain).connect(master);
  osc.start(t);
  osc.stop(t + 2.5);
}
